import { Link } from 'react-router-dom';
import { useDashboard, type DashboardStats } from '@/api/queries/dashboard';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowUpRight,
  AlertTriangle,
  Clock,
  Inbox as InboxIcon,
  Building2,
  Sparkles,
  Phone,
  Globe,
  Camera,
  CheckCircle2,
  Target,
} from 'lucide-react';
import { cn } from '@/lib/cn';

const FUNIL_ORDER = [
  'lista', 'abertura', 'qualificacao', 'call_diagnostico', 'diagnostico_pago',
  'proposta', 'objecao', 'fechamento', 'onboarding', 'retencao', 'perdido', 'dead_onb',
];

const FUNIL_LABELS: Record<string, string> = {
  lista: 'lista', abertura: 'abertura', qualificacao: 'qualificação',
  call_diagnostico: 'call diag.', diagnostico_pago: 'diag. pago', proposta: 'proposta',
  objecao: 'objeção', fechamento: 'fechamento', onboarding: 'onboarding',
  retencao: 'retenção', perdido: 'perdido', dead_onb: 'dead onb',
};

const tempDot: Record<string, string> = {
  frio: 'bg-[color:var(--color-temp-frio)]',
  morno: 'bg-[color:var(--color-temp-morno)]',
  quente: 'bg-[color:var(--color-temp-quente)]',
};

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `R$ ${(n / 1_000).toFixed(1)}k`;
  return `R$ ${n.toFixed(0)}`;
}

function Stat({
  label, value, hint, to, highlight,
}: {
  label: string; value: string; hint?: string; to?: string;
  highlight?: 'destructive' | 'primary' | null;
}) {
  const inner = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">{label}</p>
        {to && <ArrowUpRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={2.25} />}
      </div>
      <p className={cn(
        'mt-1 mono text-[28px] font-semibold leading-none tracking-[-0.025em] tabular',
        highlight === 'destructive' && 'text-destructive',
        highlight === 'primary' && 'text-primary',
      )}>{value}</p>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </>
  );
  const cls = 'group block rounded-md border border-border/70 bg-card p-4 transition-colors hover:border-border';
  return to ? <Link to={to} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>;
}

function FunnelBar({ stats }: { stats: DashboardStats }) {
  const total = stats.empresas_total || 1;
  const max = Math.max(...FUNIL_ORDER.map((k) => stats.empresas_por_funil?.[k] ?? 0));
  return (
    <div className="rounded-md border border-border/70 bg-card p-4">
      <h3 className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Funil — distribuição</h3>
      <div className="mt-3 grid gap-1.5">
        {FUNIL_ORDER.map((k) => {
          const c = stats.empresas_por_funil?.[k] ?? 0;
          const pct = max ? (c / max) * 100 : 0;
          const pctTotal = (c / total) * 100;
          return (
            <Link key={k} to={`/empresas?status_funil=${k}`} className="grid grid-cols-[110px_1fr_60px] items-center gap-2 group">
              <span className="text-[12px] text-foreground/85 truncate lowercase tracking-tight group-hover:text-foreground">
                {FUNIL_LABELS[k] ?? k}
              </span>
              <div className="h-2 rounded-sm bg-muted overflow-hidden">
                <div className="h-full bg-foreground/15 transition-all group-hover:bg-primary" style={{ width: `${pct}%` }} />
              </div>
              <span className="mono text-[11px] text-muted-foreground tabular text-right">
                {c} · {pctTotal.toFixed(0)}%
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function TemperaturaSplit({ stats }: { stats: DashboardStats }) {
  const total = ['frio', 'morno', 'quente'].map((k) => stats.empresas_por_temperatura?.[k] ?? 0).reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="rounded-md border border-border/70 bg-card p-4">
      <h3 className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Temperatura</h3>
      <div className="mt-3 space-y-2">
        {(['quente', 'morno', 'frio'] as const).map((k) => {
          const c = stats.empresas_por_temperatura?.[k] ?? 0;
          const pct = (c / total) * 100;
          return (
            <div key={k} className="grid grid-cols-[80px_1fr_50px] items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-[12px] lowercase">
                <span className={cn('h-1.5 w-1.5 rounded-full', tempDot[k])} />{k}
              </span>
              <div className="h-2 rounded-sm bg-muted overflow-hidden">
                <div className={cn('h-full transition-all', tempDot[k])} style={{ width: `${pct}%`, opacity: 0.5 }} />
              </div>
              <span className="mono text-[11px] text-muted-foreground tabular text-right">{c}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EnrichCard({ stats }: { stats: DashboardStats }) {
  const p = stats.enrich_progress;
  if (!p) return null;
  const pct = p.total ? (p.enriquecidas / p.total) * 100 : 0;
  return (
    <div className="rounded-md border border-border/70 bg-card p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Enrichment</h3>
        <span className="mono text-[11px] tabular text-muted-foreground">{pct.toFixed(1)}%</span>
      </div>
      <div>
        <p className="mono text-[28px] font-semibold leading-none tracking-[-0.025em] tabular">
          {p.enriquecidas.toLocaleString('pt-BR')}
          <span className="text-base text-muted-foreground/70"> / {p.total.toLocaleString('pt-BR')}</span>
        </p>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div>
          <p className="text-muted-foreground">fila</p>
          <p className="mono tabular font-medium">{p.pendentes.toLocaleString('pt-BR')}</p>
        </div>
        <div>
          <p className="text-muted-foreground">sem cnpj</p>
          <p className="mono tabular font-medium">{p.sem_cnpj.toLocaleString('pt-BR')}</p>
        </div>
        <div>
          <p className="text-muted-foreground">falhas</p>
          <p className={cn('mono tabular font-medium', p.falhas > 0 && 'text-destructive')}>
            {p.falhas.toLocaleString('pt-BR')}
          </p>
        </div>
      </div>
    </div>
  );
}

function SinaisCard({ stats }: { stats: DashboardStats }) {
  const s = stats.sinais;
  if (!s) return null;
  const total = stats.empresas_total || 1;
  const items: Array<{ icon: React.ComponentType<{ className?: string; strokeWidth?: number }>; label: string; value: number; to?: string }> = [
    { icon: Phone, label: 'com telefone', value: s.com_telefone, to: '/triage?tem_telefone=true' },
    { icon: Globe, label: 'com site', value: s.com_site, to: '/triage?tem_site=true' },
    { icon: Camera, label: 'com redes', value: s.com_redes, to: '/triage?tem_redes=true' },
    { icon: CheckCircle2, label: 'situação ativa', value: s.ativa, to: '/triage?situacao_ativa=true' },
  ];
  return (
    <div className="rounded-md border border-border/70 bg-card p-4 space-y-2">
      <h3 className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Sinais de contato</h3>
      <div className="space-y-1.5">
        {items.map(({ icon: Icon, label, value, to }) => {
          const pct = (value / total) * 100;
          const inner = (
            <div className="grid grid-cols-[20px_100px_1fr_60px] items-center gap-2 group">
              <Icon className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" strokeWidth={2.25} />
              <span className="text-[12px] lowercase">{label}</span>
              <div className="h-2 rounded-sm bg-muted overflow-hidden">
                <div className="h-full bg-emerald-500/60 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <span className="mono text-[11px] tabular text-right text-muted-foreground">
                {value} · {pct.toFixed(0)}%
              </span>
            </div>
          );
          return to ? <Link key={label} to={to}>{inner}</Link> : <div key={label}>{inner}</div>;
        })}
      </div>
    </div>
  );
}

function ScoreDistribution({ stats }: { stats: DashboardStats }) {
  const dist = stats.score_distribution;
  if (!dist) return null;
  const max = Math.max(...Object.values(dist));
  const total = Object.values(dist).reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="rounded-md border border-border/70 bg-card p-4 space-y-2">
      <h3 className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Distribuição de score</h3>
      <div className="flex items-end gap-1 h-24">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((s) => {
          const c = dist[s.toString()] ?? 0;
          const h = max ? (c / max) * 100 : 0;
          const tone = s >= 8 ? 'bg-emerald-500/70' : s >= 5 ? 'bg-amber-500/60' : s >= 2 ? 'bg-slate-400/50' : 'bg-rose-400/40';
          return (
            <Link
              key={s}
              to={`/triage?score_min=${s}`}
              className="flex flex-1 flex-col items-center justify-end gap-1 group"
              title={`score ${s}: ${c} (${((c / total) * 100).toFixed(1)}%)`}
            >
              <span className={cn('mono text-[9px] tabular text-muted-foreground/70 group-hover:text-foreground', c === 0 && 'opacity-30')}>
                {c}
              </span>
              <div className={cn('w-full rounded-t-sm transition-all min-h-1', tone)} style={{ height: `${h}%` }} />
              <span className="mono text-[10px] tabular text-muted-foreground">{s}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function TopList({
  title, items, formatLabel, formatPath,
}: {
  title: string;
  items?: Array<{ c: number; [k: string]: unknown }>;
  formatLabel: (item: { c: number; [k: string]: unknown }) => string;
  formatPath: (item: { c: number; [k: string]: unknown }) => string;
}) {
  if (!items || items.length === 0) return null;
  const max = Math.max(...items.map((i) => i.c));
  return (
    <div className="rounded-md border border-border/70 bg-card p-4 space-y-2">
      <h3 className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">{title}</h3>
      <div className="space-y-1">
        {items.map((item, idx) => {
          const label = formatLabel(item);
          const path = formatPath(item);
          const pct = max ? (item.c / max) * 100 : 0;
          return (
            <Link
              key={`${label}-${idx}`}
              to={path}
              className="grid grid-cols-[1fr_40px] items-center gap-2 group"
            >
              <div className="min-w-0">
                <p className="truncate text-[12px] group-hover:text-primary">{label}</p>
                <div className="mt-0.5 h-1 rounded-sm bg-muted overflow-hidden">
                  <div className="h-full bg-foreground/20 group-hover:bg-primary transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <span className="mono text-[11px] tabular text-right text-muted-foreground">{item.c}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { data: stats, isLoading } = useDashboard();

  if (isLoading || !stats) {
    return (
      <div className="space-y-5">
        <div className="border-b border-border/70 pb-3">
          <h1 className="text-[28px] font-semibold tracking-[-0.02em] leading-tight">Visão geral</h1>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="border-b border-border/70 pb-3">
        <h1 className="text-[28px] font-semibold tracking-[-0.02em] leading-tight">Visão geral</h1>
        <p className="mt-0.5 text-xs text-muted-foreground">O estado do funil agora.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="empresas" value={stats.empresas_total.toLocaleString('pt-BR')} hint="no funil" to="/empresas" />
        <Stat label="tarefas hoje" value={String(stats.tarefas_hoje)} hint={`${stats.tarefas_pendentes} pendentes`} to="/inbox" highlight={stats.tarefas_hoje > 0 ? 'primary' : null} />
        <Stat label="sla estourado" value={String(stats.tarefas_sla_estourado)} hint="precisam de atenção" to="/inbox?filter=estouradas" highlight={stats.tarefas_sla_estourado > 0 ? 'destructive' : null} />
        <Stat label="pipeline mensal" value={formatCurrency(stats.pipeline_valor_estimado)} hint={`${stats.oportunidades_em_negociacao} em negociação`} to="/pipeline" />
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <EnrichCard stats={stats} />
        <SinaisCard stats={stats} />
        <ScoreDistribution stats={stats} />
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <FunnelBar stats={stats} />
        </div>
        <TemperaturaSplit stats={stats} />
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <TopList
          title="Top CNAEs"
          items={stats.top_cnaes as unknown as Array<{ c: number; [k: string]: unknown }> | undefined}
          formatLabel={(it) => {
            const cnae = it.cnae as string | undefined;
            const desc = it.desc as string | undefined;
            return cnae ? `${cnae}${desc ? ` · ${desc}` : ''}` : desc ?? '—';
          }}
          formatPath={(it) => `/triage?cnae=${encodeURIComponent((it.cnae as string) ?? '')}`}
        />
        <TopList
          title="Top bairros"
          items={stats.top_bairros as unknown as Array<{ c: number; [k: string]: unknown }> | undefined}
          formatLabel={(it) => (it.bairro as string) ?? '—'}
          formatPath={(it) => `/empresas?q=${encodeURIComponent((it.bairro as string) ?? '')}`}
        />
        <TopList
          title="Top cidades"
          items={stats.top_cidades as unknown as Array<{ c: number; [k: string]: unknown }> | undefined}
          formatLabel={(it) => (it.cidade as string) ?? '—'}
          formatPath={(it) => `/empresas?cidade=${encodeURIComponent((it.cidade as string) ?? '')}`}
        />
      </div>

      <div className="rounded-md border border-border/70 bg-card p-4">
        <h3 className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Próximos passos</h3>
        <div className="mt-3 flex flex-wrap gap-2 text-[13px]">
          <Link to="/triage?score_min=7&situacao_ativa=true&tem_telefone=true" className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-50 px-2.5 py-1 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200 hover:bg-emerald-100">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.25} />
            triage: score 7+ ativas com telefone
          </Link>
          <Link to="/inbox" className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-background px-2.5 py-1 hover:bg-muted">
            <InboxIcon className="h-3.5 w-3.5" strokeWidth={2.25} />
            ir pra inbox
          </Link>
          <Link to="/pipeline" className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-background px-2.5 py-1 hover:bg-muted">
            <Building2 className="h-3.5 w-3.5" strokeWidth={2.25} />
            ver pipeline
          </Link>
          <Link to="/triage" className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-background px-2.5 py-1 hover:bg-muted">
            <Target className="h-3.5 w-3.5" strokeWidth={2.25} />
            triage completa
          </Link>
          {stats.tarefas_sla_estourado > 0 && (
            <Link to="/inbox?filter=estouradas" className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1 text-destructive hover:bg-destructive/10">
              <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.25} />
              limpar {stats.tarefas_sla_estourado} tarefa{stats.tarefas_sla_estourado === 1 ? '' : 's'} atrasada{stats.tarefas_sla_estourado === 1 ? '' : 's'}
            </Link>
          )}
          {stats.tarefas_hoje > 0 && (
            <Link to="/inbox" className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-2.5 py-1 text-primary hover:bg-primary/10">
              <Clock className="h-3.5 w-3.5" strokeWidth={2.25} />
              {stats.tarefas_hoje} tarefa{stats.tarefas_hoje === 1 ? '' : 's'} pra hoje
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
